import * as React from "react";
import {
  DataGrid,
  DataGridProps,
  GridCallbackDetails,
  GridColumnHeaders,
  GridColumnVisibilityModel,
  GridRow,
  GridRowIdGetter,
  GridRowParams,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValidRowModel,
  GridRowClassNameParams,
  ptBR,
} from "@mui/x-data-grid";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import styles from "./FileUploadTable.module.css";
import dayjs from "dayjs";

const MemoizedRow = React.memo(GridRow);
const MemoizedColumnHeaders = React.memo(GridColumnHeaders);
type RowData = {
  id: number | string;
};

interface DataGridCustomProps<T extends GridValidRowModel>
  extends Omit<DataGridProps, "getRowId"> {
  getRowId?: GridRowIdGetter<T>;
  getRowClassName?: (params: GridRowClassNameParams) => string;
  rows: T[];
  columns: any[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails
  ) => void;
  customButton?: any;
  checkboxSelection?: boolean;
  isRowSelectable?: (params: GridRowParams) => boolean;
  onRowSelectionModelChange?: (
    newRowSelectionModel: GridRowSelectionModel
  ) => void;
  rowSelectionModel?: GridRowSelectionModel;
  loading?: boolean;
  pageSize?: 1 | 3 | 5 | 10 | 15 | 20 | 25 | 50 | 100;
  headerHeight?: number;
  isReversal?: boolean;
  rowCountState?: number;
  paginationModel?: any;
  setPaginationModel?: (paginationModel: any) => void;
  handleExport?: (event: any) => void;
  gridApiRef?: any;
}
const DataGridCustom = <T extends RowData>({
  rows,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  customButton,
  getRowId,
  checkboxSelection,
  isRowSelectable,
  onRowSelectionModelChange,
  rowSelectionModel,
  loading,
  pageSize,
  getRowClassName,
  headerHeight = 56,
  isReversal = false,
  rowCountState,
  paginationModel,
  setPaginationModel,
  handleExport,
  gridApiRef,
}: DataGridCustomProps<T>) => {
  const theme = useTheme();
  const isVisibled = useMediaQuery(theme.breakpoints.up("sm"));
  const fieldsToExport = localStorage.getItem("selectedFields");
  const storedFieldsArray = fieldsToExport ? fieldsToExport.split(",") : [];
  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        {customButton}
        {isVisibled && <GridToolbarColumnsButton />}
        {isVisibled && <GridToolbarFilterButton />}
        {isVisibled && <GridToolbarDensitySelector />}
        {isReversal && storedFieldsArray.length === 0 && (
          <GridToolbarExport
            csvOptions={{
              fileName: `ExportData-${dayjs().format("DD-MM-YYYY-HH-mm")}`,
              delimiter: ";",
              utf8WithBom: true,
              allColumns: true,
            }}
            printOptions={{
              disableToolbarButton: true,
            }}
          />
        )}
      </GridToolbarContainer>
    );
  };
  return (
    <Box
      sx={{
        [theme.breakpoints.down("lg")]: {
          width: `calc(100vw - ${theme.spacing(4)})`,
        },
      }}
    >
      <DataGrid
        apiRef={gridApiRef}
        getRowHeight={() => "auto"}
        rows={rows}
        columns={columns}
        getRowClassName={(params: GridRowClassNameParams) => {
          const customClassName = getRowClassName?.(params);
          const moduleClassName = customClassName
            ? ` ${styles[customClassName]}`
            : "";
          return `MuiDataGrid-row${moduleClassName}`;
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        getRowId={getRowId ?? ((row: T) => row.id)}
        slots={{
          toolbar: CustomToolbar,
          row: MemoizedRow,
          columnHeaders: MemoizedColumnHeaders,
        }}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: pageSize ?? 10,
            },
          },
        }}
        pageSizeOptions={[1, 3, 5, 10, 15, 20, 25, 50, 100]}
        disableRowSelectionOnClick
        checkboxSelection={checkboxSelection}
        isRowSelectable={isRowSelectable}
        columnHeaderHeight={headerHeight}
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        sx={{
          "& .MuiDataGrid-overlayWrapper ": {
            height: rows.length === 0 ? "100px" : "0px",
          },
          "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
            {
              outline: "none !important",
            },
          "&  .MuiDataGrid-columnHeaderTitle": {
            overflow: "hidden",
            lineHeight: "20px",
            whiteSpace: "normal",
            height: "auto",
          },
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgb(37, 37, 38)"
              : "rgb(255, 255, 255)",

          padding: theme.spacing(2),
        }}
        paginationModel={paginationModel}
        rowCount={rowCountState}
        paginationMode={rowCountState ? "server" : "client"}
        onPaginationModelChange={setPaginationModel}
        loading={loading}
      />
    </Box>
  );
};
export default DataGridCustom;
